import React, { useEffect, useState } from "react";
import { api, fmtMoney, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Star } from "@phosphor-icons/react";

const empty = { name: "", phone: "", email: "" };

export default function Customers() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/customers").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone are required");
      return;
    }
    const payload = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim() || null,
    };
    try {
      await api.post("/customers", payload);
      toast.success("Added");
      setOpen(false);
      setForm(empty);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="CRM"
        title="Customers & loyalty"
        subtitle="See top spenders, manage loyalty points, and capture every visit's history."
        actions={<Button onClick={() => setOpen(true)} className="btn-primary" data-testid="cust-add-btn"><Plus size={16} weight="bold"/> Add customer</Button>}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 stagger">
        {list.map((c) => (
          <div key={c.id} className="bento p-5 flex items-center gap-4" data-testid={`cust-${c.id}`}>
            <div className="w-14 h-14 rounded-full bg-[var(--brand-2)] text-white grid place-items-center font-display font-extrabold text-xl">
              {c.name[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-lg">{c.name}</div>
              <div className="text-xs text-[var(--text-secondary)]">{c.phone} {c.email ? `· ${c.email}` : ""}</div>
              <div className="flex items-center gap-4 mt-2 text-xs">
                <span><span className="font-display font-bold text-[var(--brand)]">{c.total_orders}</span> orders</span>
                <span>spent <span className="font-display font-bold">{fmtMoney(c.total_spent)}</span></span>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-1 text-[var(--brand)]">
                <Star size={16} weight="fill"/>
                <span className="font-display font-extrabold text-2xl">{c.loyalty_points}</span>
              </div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-secondary)]">Loyalty pts</div>
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="bento p-8 text-center text-sm text-[var(--text-secondary)] col-span-full">No customers yet.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="cust-dialog">
          <DialogHeader><DialogTitle>Add customer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-eyebrow">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="cust-form-name"/>
            </div>
            <div>
              <Label className="label-eyebrow">Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" data-testid="cust-form-phone"/>
            </div>
            <div>
              <Label className="label-eyebrow">Email (optional)</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="cust-form-email"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} className="btn-primary" data-testid="cust-form-save">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
